import { Contact, Database, Mail, Rocket } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useOsintCredentialStatuses } from './hooks/useOsintCredentialStatuses';
import {
  InfraCredentialCard,
  type InfraCredentialSource,
} from './InfraCredentialCard';
import { InfraCredentialConfigDialog } from './InfraCredentialConfigDialog';

/**
 * Human Intelligence Gathering Sources — system-wide (Global) API-key
 * credentials for the passive people/identity connectors (Hunter.io, Lusha,
 * Apollo, Dehashed). Each is an operator-global master key stored via the
 * enrichment key store (`/enrichment/{plugin}/key`); the capability that
 * consumes it resolves the key at run time.
 *
 * NOTE: Apollo's enrichment plugin id is `pius_apollo` (it reuses the existing
 * pius credential), so it also appears in the Discovery panel's paid-enrichment
 * grid under the same key — configuring it here configures it there too.
 *
 * Styled to match the "Paid Enrichment Sources" grid: one status card per source
 * (`InfraCredentialCard` — icon + name + description + Configured/Not-Configured
 * chip + Edit) and an Edit dialog (`InfraCredentialConfigDialog`) for entering
 * the key. Unlike the pius enrichment cards there is NO enable/disable toggle and
 * NO credits line: for these creds, configured == active.
 *
 * `SOURCES` is the frontend allowlist (single source of truth for which creds
 * this panel surfaces); each source's configured flag comes from a per-plugin
 * `GET /enrichment/{plugin}/key`.
 */

const SOURCES: readonly InfraCredentialSource[] = [
  {
    id: 'hunter',
    label: 'Hunter.io',
    description:
      'Email pattern discovery and verification for an organization.',
    Icon: Mail,
  },
  {
    id: 'lusha',
    label: 'Lusha',
    description: 'Contact enrichment (emails, phone) for individuals.',
    Icon: Contact,
  },
  {
    // Apollo reuses the existing pius credential, so the key routes use the
    // `pius_apollo` plugin id even though the label is "Apollo".
    id: 'pius_apollo',
    label: 'Apollo',
    description: 'Company and contact data enrichment for organizations.',
    Icon: Rocket,
  },
  {
    id: 'dehashed',
    label: 'Dehashed',
    description:
      'Breach and leaked-credential lookup for domains and individuals.',
    Icon: Database,
  },
] as const;

const SOURCE_IDS = SOURCES.map(source => source.id);

export function HumanIntelligenceCredentialsPanel() {
  const { data, status, refetch } = useOsintCredentialStatuses(SOURCE_IDS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const configuredById = useMemo(() => {
    const map = new Map<string, boolean>();
    if (status === 'success') {
      for (const credential of data) {
        map.set(credential.id, credential.configured);
      }
    }
    return map;
  }, [data, status]);

  const isError = status === 'error';
  const selected = SOURCES.find(source => source.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="mb-3">
          <h2 className="text-chariot-text-primary text-sm font-semibold">
            Human Intelligence Gathering Sources
          </h2>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Paid people/identity connectors that require an API key for
            activation
          </p>
        </div>

        {isError && (
          <div className="mb-3 flex items-center gap-2 text-xs">
            <span className="text-chariot-status-warning">
              Couldn&apos;t load credential status.
            </span>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-chariot-text-primary underline underline-offset-2 hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3">
          {SOURCES.map(source => (
            <InfraCredentialCard
              key={source.id}
              source={source}
              configured={configuredById.get(source.id) ?? false}
              // Guard: a coming-soon source can never open the config dialog,
              // even if an Edit affordance is somehow triggered.
              onEdit={() => !source.comingSoon && setSelectedId(source.id)}
            />
          ))}
        </div>
      </section>

      <InfraCredentialConfigDialog
        source={
          selected
            ? {
                id: selected.id,
                label: selected.label,
                Icon: selected.Icon,
                configured: configuredById.get(selected.id) ?? false,
              }
            : null
        }
        onOpenChange={open => !open && setSelectedId(null)}
      />
    </div>
  );
}
